// BioForge 2.2 — Venture Workspace: tab router + 12 tab views.
import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight, Quote, Wrench, Activity, ShieldCheck, FlaskConical, Building2,
  AlertOctagon, TrendingUp, Lightbulb, X, ChevronRight, Loader2, BookOpen,
  ExternalLink, Cpu, FileText, Layers, Clock, Target, Users, Share2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { formatINR } from "@/currency";
import { LAYER_INFO, resolveLayer } from "@/layers";
import {
  useProject, useRunStream, apiGet, apiPost, formatUSD, AGENTS, AGENT_BY_EVENT,
} from "@/lib";
import { TechConvergence, RiskMatrix, VentureRadar, MarketFunnel, WorkflowGraph, NumberCountUp,
} from "@/viz";
import { FiveLayerArchitecture } from "@/discover";
import { useInsights } from "@/shell";
import { PersonaBanner, TRLCard } from "@/persona";
import { FounderCopilot } from "@/copilot";
import { ventureHeroFor } from "@/scenario_images";
import { PitchDeck } from "@/pitch";
import { FinalReport } from "@/report";

const TABS = [
  "overview", "innovation", "prototype", "validation", "regulatory", "clinical",
  "commercial", "founder-review", "investor", "scorecard", "report", "pitch", "workflow", "trace",
];

export function VentureWorkspace() {
  const { id, tab } = useParams();
  const navigate = useNavigate();
  const { doc, loading, refetch } = useProject(id, { pollIfRunning: true });
  const stream = useRunStream(id, doc);
  const active = TABS.includes(tab) ? tab : "overview";

  useEffect(() => { if (!TABS.includes(tab)) navigate(`/projects/${id}/overview`, { replace: true }); }, [tab, id, navigate]);

  // Phase 5 — Founder Copilot replaces tab-specific insights on every venture page.
  useInsights(doc ? <FounderCopilot doc={doc} /> : null, [doc, active]);

  if (loading && !doc) return <div className="p-8"><Skeleton className="h-32 w-full" /></div>;
  if (!doc) return <div className="p-8 text-sm text-white/50">Venture not found.</div>;

  const tabKey = `${id}-${active}`;
  const props = { doc, stream, id, refetch };

  const hideHeader = active === "pitch";

  return (
    <div className="h-full flex flex-col" data-testid="venture-workspace">
      {!hideHeader && <WorkspaceHeader doc={doc} stream={stream} id={id} active={active} />}
      <div className="flex-1 min-h-0">
        <AnimatePresence mode="wait">
          <motion.div key={tabKey}
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2, ease: [0.2, 0.7, 0.25, 1] }}
            className="h-full">
            {active === "overview" && <Overview {...props} />}
            {active === "innovation" && <Innovation {...props} />}
            {active === "prototype" && <Prototype {...props} />}
            {active === "validation" && <Validation {...props} />}
            {active === "regulatory" && <Regulatory {...props} />}
            {active === "clinical" && <Clinical {...props} />}
            {active === "commercial" && <Commercial {...props} />}
            {active === "founder-review" && <FounderReviewTab {...props} />}
            {active === "investor" && <InvestorMode {...props} />}
            {active === "scorecard" && <Scorecard {...props} />}
            {active === "report" && <FinalReport doc={doc} id={id} />}
            {active === "pitch" && <PitchDeck {...props} />}
            {active === "workflow" && <WorkflowTab {...props} />}
            {active === "trace" && <TraceView id={id} />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

function WorkspaceHeader({ doc, stream, id, active }) {
  const navigate = useNavigate();
  const status = stream.runStatus || doc.status;
  const title = doc.final_report?.company_name || doc.inputs?.problem || "Untitled venture";
  const cancel = async () => { try { await apiPost(`/runs/${id}/cancel`, {}); toast.success("Cancelled"); } catch (e) { toast.error(e.message); } };
  return (
    <header className="shrink-0 px-6 py-3 border-b border-white/[0.06] flex items-center justify-between gap-4 bg-white/[0.012]">
      <div className="flex items-center gap-3 min-w-0">
        <button onClick={() => navigate("/projects")} className="text-white/40 hover:text-white/80 text-xs uppercase tracking-wider" data-testid="back-pipeline">Pipeline</button>
        <ChevronRight className="w-3 h-3 text-white/30" />
        <div className="min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <h1 data-testid="workspace-title" className="text-base font-semibold tracking-tight truncate">{title}</h1>
            <span className="text-[10px] mono text-white/30 shrink-0">{id.slice(0, 8)}</span>
          </div>
          <div className="text-[10px] text-white/40 capitalize">{active.replace(/-/g, " ")}</div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <StatusPill status={status} />
        {status === "running" && <Button size="sm" variant="outline" onClick={cancel} data-testid="cancel-run"><X className="w-3 h-3 mr-1" />Cancel</Button>}
      </div>
    </header>
  );
}

function StatusPill({ status }) {
  const map = {
    complete: { dot: "bg-emerald-400", text: "text-emerald-200", border: "border-emerald-400/30", bg: "bg-emerald-400/10" },
    running:  { dot: "bg-teal-300 pulse-dot", text: "text-teal-200", border: "border-teal-400/30", bg: "bg-teal-400/10" },
    failed:   { dot: "bg-rose-400", text: "text-rose-200", border: "border-rose-400/30", bg: "bg-rose-400/10" },
    cancelled:{ dot: "bg-white/40", text: "text-white/60", border: "border-white/15", bg: "bg-white/[0.04]" },
  };
  const m = map[status] || map.cancelled;
  return (
    <span data-testid={`status-${status}`} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] uppercase tracking-wider border ${m.text} ${m.border} ${m.bg}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />{status}
    </span>
  );
}

// ===========================================================================
// Common utilities
// ===========================================================================
function Empty({ label }) { return <div className="p-12 text-center text-sm text-white/40 italic">{label}</div>; }
function H2({ children }) { return <h2 className="text-[10px] uppercase tracking-[0.18em] text-white/40">{children}</h2>; }
function GlassPanel({ className = "", children, testId, title, action }) {
  return (
    <section data-testid={testId} className={`glass rounded-xl border-white/[0.06] flex flex-col overflow-hidden ${className}`}>
      {title && (
        <div className="bento-header">
          <span>{title}</span>{action}
        </div>
      )}
      <div className="flex-1 min-h-0">{children}</div>
    </section>
  );
}
function severityClasses(sev) {
  return {
    Low: { bg: "bg-emerald-400/10", text: "text-emerald-200", border: "border-emerald-400/30" },
    Medium: { bg: "bg-amber-400/10", text: "text-amber-200", border: "border-amber-400/30" },
    High: { bg: "bg-orange-400/10", text: "text-orange-200", border: "border-orange-400/30" },
    Critical: { bg: "bg-rose-400/15", text: "text-rose-200", border: "border-rose-400/40" },
  }[sev] || { bg: "bg-white/[0.04]", text: "text-white/60", border: "border-white/15" };
}
function SevPill({ severity }) {
  const c = severityClasses(severity);
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${c.bg} ${c.text} border ${c.border}`}>{severity || "—"}</span>;
}

// ===========================================================================
// LAYER STRIP — colored badge + italic layer_contribution under each agent tab
// ===========================================================================
function LayerStrip({ doc, event }) {
  const { layer, layer_contribution, isLegacy } = resolveLayer(doc, event);
  const info = LAYER_INFO[layer] || LAYER_INFO.meta;
  return (
    <div className="flex items-start gap-3" data-testid={`layer-strip-${event}`}>
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] uppercase tracking-[0.14em] font-medium shrink-0 ${info.badgeClass}`}
        style={info.badgeStyle}
        data-testid={`layer-badge-${layer}`}
      >
        <span
          className={`w-1.5 h-1.5 rounded-full ${info.dotClass}`}
          style={info.dotStyle}
        />
        {info.n != null ? `Layer ${info.n} · ${info.label}` : info.label}
      </span>
      {layer_contribution ? (
        <span className="text-[12px] italic text-white/55 leading-snug pt-1 line-clamp-2">
          {layer_contribution}
        </span>
      ) : (
        <span className="text-[11px] italic text-white/35 pt-1" data-testid={`layer-legacy-${event}`}>
          {isLegacy ? "Legacy run — layer contribution not recorded" : "—"}
        </span>
      )}
    </div>
  );
}

// ===========================================================================
// OVERVIEW — single screen, no scroll
// ===========================================================================
function Overview({ doc, stream }) {
  const fin = stream.finalReport || doc.final_report || {};
  const fr = stream.founderReview || doc.founder_review;
  const sc = stream.scores || doc.scores || {};
  const com = doc.outputs?.commercialization_agent;
  const pds = fin.pitch_deck_summary || {};

  if (!fin.company_name) return <Empty label="Overview unlocks once the final report is generated." />;

  return (
    <div className="h-full p-5 overflow-y-auto" data-testid="tab-overview">
      {/* Zone 1 — OVERVIEW */}
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.24, ease: "easeOut" }}
        className="flex flex-col gap-3"
        data-testid="overview-zone-1"
      >
        <ZoneLabel label="Overview" />
        <OverviewHero doc={doc} />
        <PersonaBanner doc={doc} />
      </motion.section>

      {/* Zone 2 — INSIGHT */}
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.24, ease: "easeOut" }}
        className="flex flex-col gap-3 pt-12"
        data-testid="overview-zone-2"
      >
        <ZoneLabel label="Insight" />
        <FiveLayerArchitecture doc={doc} />
        <TechConvergence problem={doc.inputs?.problem} technologies={fin.technology_stack} companyName={fin.company_name} />
      </motion.section>

      {/* Zone 3 — OUTCOMES */}
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.24, ease: "easeOut" }}
        className="pt-12 pb-6"
        data-testid="overview-zone-3"
      >
        <ZoneLabel label="Outcomes" />
        <div className="grid grid-cols-12 gap-3 mt-3">
          <GlassPanel className="col-span-5" testId="overview-verdict" title="Founder Verdict">
            <div className="p-5">
              {fr ? (
                <>
                  <Quote className="w-5 h-5 text-teal-300/60 mb-2" />
                  <div className="text-base italic leading-relaxed line-clamp-5">"{fr.overall_recommendation}"</div>
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <VerdictPill text={fr.overall_recommendation} />
                    <span className="text-[10px] mono text-white/50">conf {fr.confidence_score}</span>
                  </div>
                </>
              ) : <div className="text-sm text-white/40 italic">Pending…</div>}
            </div>
          </GlassPanel>

          <GlassPanel className="col-span-4" testId="overview-scores" title="Key Scores">
            <div className="p-4 grid grid-cols-2 gap-2">
              {[
                ["innovation_score", "Innovation"],
                ["feasibility_score", "Feasibility"],
                ["investor_attractiveness_score", "Investor"],
                ["regulatory_readiness_score", "Regulatory"],
              ].map(([k, label]) => (
                <ScoreCircle key={k} label={label} value={sc[k]?.score} />
              ))}
              <div className="col-span-2"><TRLCard doc={doc} /></div>
            </div>
          </GlassPanel>

          <GlassPanel className="col-span-3" testId="overview-market" title="Market">
            <div className="p-3 flex items-center justify-center h-full">
              <MarketFunnel tam={com?.tam_usd} sam={com?.sam_usd} som={com?.som_usd} />
            </div>
          </GlassPanel>

          {[["problem", "Problem"], ["solution", "Solution"], ["technology", "Technology"], ["why_now", "Why Now"]].map(([k, label]) => (
            <GlassPanel key={k} className="col-span-3" testId={`overview-${k}`} title={label}>
              <div className="p-4 text-sm text-white/80 leading-relaxed line-clamp-5">{pds[k] || "—"}</div>
            </GlassPanel>
          ))}
        </div>
      </motion.section>
    </div>
  );
}

function ZoneLabel({ label }) {
  return (
    <div className="flex items-center gap-3" data-testid={`zone-label-${label.toLowerCase()}`}>
      <span className="text-[10px] uppercase tracking-[0.2em] text-white/30">{label}</span>
      <div className="h-px flex-1 bg-gradient-to-r from-white/10 to-transparent" />
    </div>
  );
}

function VerdictPill({ text }) {
  const t = (text || "").toLowerCase();
  if (t.includes("pivot")) return <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-amber-400/10 text-amber-200 border border-amber-400/30">Pivot</span>;
  if (t.includes("proceed") && /(caveat|de-scope|only if)/.test(t)) return <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-amber-400/10 text-amber-200 border border-amber-400/30">Proceed w/ caveats</span>;
  if (t.includes("proceed")) return <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-400/10 text-emerald-200 border border-emerald-400/30">Strong proceed</span>;
  return <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-white/[0.04] text-white/60 border border-white/15">Hold</span>;
}

function ScoreCircle({ label, value }) {
  const v = value ?? 0;
  const color = v >= 70 ? "#5EEAD4" : v >= 50 ? "#FBBF24" : "#F87171";
  const text = v >= 70 ? "text-teal-200" : v >= 50 ? "text-amber-200" : "text-rose-200";
  const C = 2 * Math.PI * 28;
  return (
    <div className="rounded-md border border-white/[0.06] bg-white/[0.015] p-3 flex items-center gap-3">
      <svg width={64} height={64} viewBox="0 0 64 64">
        <circle cx={32} cy={32} r={28} stroke="rgba(255,255,255,0.06)" strokeWidth={4} fill="none" />
        <circle className="gauge-ring" cx={32} cy={32} r={28} stroke={color} strokeWidth={4} fill="none" strokeLinecap="round"
          strokeDasharray={C} strokeDashoffset={C * (1 - v / 100)} transform="rotate(-90 32 32)" />
      </svg>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.14em] text-white/40">{label}</div>
        <div className={`text-2xl mono font-semibold ${text}`}><NumberCountUp value={value} /></div>
      </div>
    </div>
  );
}

function OverviewHero({ doc }) {
  const hero = ventureHeroFor(doc?.run_id);
  const fin = doc?.final_report || {};
  const name = fin.company_name || "Untitled venture";
  const tagline = doc?.inputs?.problem || doc?.problem || "";
  return (
    <div className="relative overflow-hidden rounded-xl border border-white/[0.06]" style={{ height: 240 }} data-testid="overview-hero">
      {hero ? (
        <img src={hero} alt="" className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0" style={{
          background: "radial-gradient(80% 60% at 25% 30%, rgba(94,234,212,0.18) 0%, rgba(94,234,212,0) 60%), radial-gradient(60% 50% at 80% 70%, rgba(45,212,191,0.15) 0%, rgba(0,0,0,0) 65%), linear-gradient(135deg, #0F1419 0%, #0A0B0E 100%)",
        }} />
      )}
      <div className="absolute inset-0" style={{ background: hero
        ? "linear-gradient(180deg, rgba(10,11,14,0.30) 0%, rgba(10,11,14,0.55) 55%, rgba(10,11,14,0.92) 100%)"
        : "linear-gradient(180deg, rgba(10,11,14,0.1) 0%, rgba(10,11,14,0.5) 100%)"
      }} />
      <div className="absolute inset-0 flex items-end p-6">
        <div className="max-w-3xl">
          <div className="text-[10px] uppercase tracking-[0.2em] text-teal-300/90 mb-1">Venture overview</div>
          <div className="text-3xl md:text-4xl font-semibold tracking-tight text-white">{name}</div>
          {tagline && <div className="text-sm text-white/80 mt-2 line-clamp-2 max-w-2xl">{tagline}</div>}
        </div>
      </div>
    </div>
  );
}

function OverviewInsights({ fr, sc }) {
  if (!fr) return null;
  return (
    <div className="space-y-4">
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-2">Top Improvements</div>
        <ol className="space-y-2">
          {(fr.top_3_improvements || []).map((s, i) => (
            <li key={i} className="flex gap-2 text-xs text-white/80 leading-relaxed">
              <span className="shrink-0 w-5 h-5 rounded-full border border-teal-400/40 text-teal-300 text-[10px] flex items-center justify-center font-semibold">{i + 1}</span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-2">Top 3 scores</div>
        <div className="space-y-1.5">
          {[["innovation_score", "Innovation"], ["investor_attractiveness_score", "Investor"], ["feasibility_score", "Feasibility"]].map(([k, label]) => (
            <div key={k} className="flex items-center gap-2">
              <div className="text-xs text-white/60 flex-1">{label}</div>
              <div className="h-1 w-20 bg-white/[0.06] rounded overflow-hidden"><div className="h-full bg-teal-400" style={{ width: `${sc?.[k]?.score || 0}%` }} /></div>
              <div className="text-[10px] mono text-teal-200 w-7 text-right">{sc?.[k]?.score ?? "—"}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// INNOVATION — 3 concept cards in a row
// ===========================================================================
function Innovation({ doc }) {
  const ia = doc.outputs?.innovation_architect;
  if (!ia) return <Empty label="Innovation concepts unavailable yet." />;
  const concepts = (ia.concepts || []).slice(0, 3);
  return (
    <div className="h-full p-6 flex flex-col gap-4" data-testid="tab-innovation">
      <LayerStrip doc={doc} event="innovation_architect" />
      <H2>Three differentiated venture concepts</H2>
      <div className="grid grid-cols-3 gap-3 flex-1 min-h-0">
        {concepts.map((c, i) => (
          <motion.div key={i}
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
            data-testid={`concept-${i}`}
            className="glass rounded-xl border-white/[0.06] p-5 flex flex-col">
            <div className="text-[10px] uppercase tracking-[0.18em] text-teal-300/80 mb-1">Concept {i + 1}</div>
            <div className="text-lg font-semibold mb-2">{c.name}</div>
            <div className="text-sm text-white/70 leading-relaxed mb-3 flex-1">{c.differentiator}</div>
            {c.key_components && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-white/40 mb-1">Key components</div>
                <div className="flex flex-wrap gap-1">
                  {c.key_components.map((k, j) => (
                    <span key={j} className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.04] border border-white/[0.08]">{k}</span>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// ===========================================================================
// PROTOTYPE — split layout
// ===========================================================================
function Prototype({ doc }) {
  const p = doc.outputs?.prototype_designer;
  if (!p) return <Empty label="Prototype design not generated yet." />;
  const bomTotal = (p.bill_of_materials || []).reduce((s, b) => s + (b.est_cost_usd || 0), 0);
  return (
    <div className="h-full p-6 grid grid-cols-12 gap-3 grid-rows-[auto_1fr]" data-testid="tab-prototype">
      <div className="col-span-12"><LayerStrip doc={doc} event="prototype_designer" /></div>
      <GlassPanel className="col-span-7" title="System Architecture">
        <div className="p-5 overflow-y-auto h-full">
          <p className="text-sm text-white/80 leading-relaxed mb-4">{p.system_architecture}</p>
          <H2>Modules</H2>
          <div className="flex flex-wrap gap-1.5 mt-2 mb-4">
            {(p.modules || []).map((m, i) => (<span key={i} className="text-[11px] px-2 py-1 rounded bg-white/[0.04] border border-white/[0.08]">{m}</span>))}
          </div>
          {p.software_architecture && (
            <>
              <H2>Software</H2>
              <p className="text-sm text-white/70 mt-2">{p.software_architecture}</p>
            </>
          )}
        </div>
      </GlassPanel>
      <GlassPanel className="col-span-5" title="Bill of Materials"
        action={
          <span className="text-[10px] mono text-teal-200 flex flex-col items-end leading-tight" data-testid="bom-panel-total">
            <span>${bomTotal.toFixed(2)}</span>
            <span className="text-white/45">{formatINR(bomTotal)}</span>
          </span>
        }>
        <div className="overflow-y-auto h-full p-3">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wider text-white/40">
              <tr><th className="text-left py-1">Item</th><th className="text-left">Qty</th><th className="text-right">Cost</th></tr>
            </thead>
            <tbody>
              {(p.bill_of_materials || []).map((b, i) => {
                const cost = b.est_cost_usd || 0;
                return (
                  <tr key={i} className="border-t border-white/[0.04]">
                    <td className="py-1.5">{b.item}</td>
                    <td>{b.qty}</td>
                    <td className="text-right mono leading-tight">
                      <div>${cost.toFixed(2)}</div>
                      <div className="text-[10px] text-white/40">{formatINR(cost)}</div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </GlassPanel>
    </div>
  );
}

// ===========================================================================
// VALIDATION — 4 quadrants
// ===========================================================================
function Validation({ doc }) {
  const v = doc.outputs?.validation_planner;
  if (!v) return <Empty label="Validation plan not ready yet." />;
  return (
    <div className="h-full p-6 flex flex-col gap-3" data-testid="tab-validation">
      <LayerStrip doc={doc} event="validation_planner" />
      <div className="grid grid-cols-2 grid-rows-2 gap-3 flex-1 min-h-0">
        <Quadrant title="Analytical" icon={Activity}>{v.analytical_validation_plan}</Quadrant>
        <Quadrant title="Technical" icon={Cpu}>{v.technical_validation_plan}</Quadrant>
        <Quadrant title="Pilot Study" icon={Target}>{v.pilot_study_design}</Quadrant>
        <Quadrant title="Success Criteria" icon={Layers}>
          <ul className="space-y-1.5 mt-1">
            {(v.success_criteria || []).map((s, i) => (
              <li key={i} className="text-sm text-white/80 flex gap-2"><span className="text-teal-300/70">▸</span>{s}</li>
            ))}
          </ul>
        </Quadrant>
      </div>
    </div>
  );
}
function Quadrant({ title, icon: Icon, children }) {
  return (
    <div className="glass rounded-xl border-white/[0.06] p-5 overflow-y-auto">
      <div className="flex items-center gap-2 mb-3"><Icon className="w-3.5 h-3.5 text-teal-300/80" /><H2>{title}</H2></div>
      <div className="text-sm text-white/80 leading-relaxed">{children}</div>
    </div>
  );
}

// ===========================================================================
// REGULATORY — 3 pathway columns
// ===========================================================================
function Regulatory({ doc }) {
  const r = doc.outputs?.regulatory_planner;
  if (!r) return <Empty label="Regulatory pathway not generated yet." />;
  return (
    <div className="h-full p-6 flex flex-col gap-3" data-testid="tab-regulatory">
      <LayerStrip doc={doc} event="regulatory_planner" />
      <div className="flex items-center gap-3">
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/40">Risk class</div>
        <span className="text-base font-semibold mono text-teal-200">{r.risk_classification || "—"}</span>
      </div>
      <div className="grid grid-cols-3 gap-3 flex-1 min-h-0">
        <PathCol title="India · CDSCO">{r.india_cdsco_pathway}</PathCol>
        <PathCol title="US · FDA">{r.fda_pathway}</PathCol>
        <PathCol title="EU · CE">{r.ce_pathway}</PathCol>
      </div>
      {(r.required_documentation || []).length > 0 && (
        <div className="glass rounded-xl border-white/[0.06] p-4">
          <H2>Required Documentation</H2>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {(r.required_documentation || []).map((d, i) => (
              <span key={i} className="text-[11px] px-2 py-0.5 rounded bg-white/[0.04] border border-white/[0.08]">{d}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
function PathCol({ title, children }) {
  return (
    <div className="glass rounded-xl border-white/[0.06] p-5 overflow-y-auto">
      <div className="text-[11px] uppercase tracking-[0.18em] text-teal-300/80 mb-2">{title}</div>
      <p className="text-sm text-white/80 leading-relaxed">{children}</p>
    </div>
  );
}

// ===========================================================================
// CLINICAL
// ===========================================================================
function Clinical({ doc }) {
  const t = doc.outputs?.clinical_trial_designer;
  if (!t) return <Empty label="Clinical trial plan not ready yet." />;
  return (
    <div className="h-full p-6 grid grid-cols-12 grid-rows-[auto_auto_1fr] gap-3" data-testid="tab-clinical">
      <div className="col-span-12"><LayerStrip doc={doc} event="clinical_trial_designer" /></div>
      <div className="col-span-12 grid grid-cols-4 gap-3">
        <KpiBlock icon={Users} label="Sample size" value={t.sample_size_estimate?.toLocaleString?.() ?? t.sample_size_estimate} />
        <KpiBlock icon={Clock} label="Timeline" value={`${t.timeline_months || "—"} mo`} />
        <KpiBlock icon={Target} label="Primary endpoint" value={t.primary_endpoint} small />
        <KpiBlock icon={Layers} label="Secondary endpoints" value={(t.secondary_endpoints || []).length} />
      </div>
      <GlassPanel className="col-span-4" title="Inclusion">
        <ul className="p-4 space-y-1.5 overflow-y-auto h-full">
          {(t.inclusion_criteria || []).map((c, i) => <li key={i} className="text-sm text-white/80 flex gap-2"><span className="text-emerald-300/70">+</span>{c}</li>)}
        </ul>
      </GlassPanel>
      <GlassPanel className="col-span-4" title="Exclusion">
        <ul className="p-4 space-y-1.5 overflow-y-auto h-full">
          {(t.exclusion_criteria || []).map((c, i) => <li key={i} className="text-sm text-white/80 flex gap-2"><span className="text-rose-300/70">−</span>{c}</li>)}
        </ul>
      </GlassPanel>
      <GlassPanel className="col-span-4" title="Protocol Draft">
        <div className="p-4 text-sm text-white/80 leading-relaxed overflow-y-auto h-full">{t.protocol_draft}</div>
      </GlassPanel>
    </div>
  );
}
function KpiBlock({ icon: Icon, label, value, small = false }) {
  return (
    <div className="glass rounded-xl border-white/[0.06] p-4 flex items-center gap-3">
      <Icon className="w-4 h-4 text-teal-300/80 shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.14em] text-white/40">{label}</div>
        <div className={`mono font-semibold mt-0.5 ${small ? "text-sm leading-tight line-clamp-2" : "text-xl"}`}>{value ?? "—"}</div>
      </div>
    </div>
  );
}

// ===========================================================================
// COMMERCIAL
// ===========================================================================
function Commercial({ doc }) {
  const c = doc.outputs?.commercialization_agent;
  if (!c) return <Empty label="Commercialization not generated yet." />;
  return (
    <div className="h-full p-6 flex flex-col gap-3" data-testid="tab-commercial">
      <LayerStrip doc={doc} event="commercialization_agent" />
      <div className="grid grid-cols-3 gap-3">
        {[["TAM", c.tam_usd, "rgba(94,234,212,0.35)"], ["SAM", c.sam_usd, "rgba(125,211,252,0.45)"], ["SOM", c.som_usd, "rgba(165,243,252,0.55)"]].map(([label, value, stroke]) => (
          <div key={label} className="glass rounded-xl border-white/[0.06] p-4" data-testid={`commercial-${label.toLowerCase()}`}>
            <div className="text-[10px] uppercase tracking-[0.14em] text-white/40">{label}</div>
            <div className="text-3xl mono font-semibold mt-1" style={{ color: stroke }}>{formatUSD(value)}</div>
            <div className="text-xs text-white/50 mono mt-0.5">{formatINR(value)}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-12 gap-3 flex-1 min-h-0">
        <GlassPanel className="col-span-7" title="Business Model">
          <div className="p-5 overflow-y-auto h-full text-sm text-white/80 leading-relaxed">{c.business_model}</div>
        </GlassPanel>
        <GlassPanel className="col-span-5" title="Pilot Partners">
          <div className="p-3 grid grid-cols-2 gap-2 overflow-y-auto h-full">
            {(c.pilot_partners || []).map((p, i) => (
              <div key={i} className="rounded-md border border-white/[0.06] bg-white/[0.02] p-2 text-xs text-center">{p}</div>
            ))}
          </div>
        </GlassPanel>
        <GlassPanel className="col-span-12" title="Commercialization Roadmap">
          <div className="p-5 text-sm text-white/80 leading-relaxed overflow-y-auto" style={{ maxHeight: 200 }}>{c.commercialization_roadmap}</div>
        </GlassPanel>
      </div>
    </div>
  );
}

// ===========================================================================
// FOUNDER REVIEW — Risk Matrix
// ===========================================================================
function FounderReviewTab({ doc, stream }) {
  const fr = stream.founderReview || doc.founder_review;
  const [selected, setSelected] = useState(null);

  if (!fr) return <Empty label="Founder review pending." />;
  return (
    <div className="h-full p-6 flex flex-col gap-3" data-testid="tab-founder-review">
      <LayerStrip doc={doc} event="founder_review" />
      <div className="glass rounded-xl border-white/[0.06] p-6">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="flex-1 min-w-0 max-w-3xl">
            <Quote className="w-6 h-6 text-teal-300/50 mb-2" />
            <blockquote className="text-2xl italic leading-relaxed">"{fr.overall_recommendation}"</blockquote>
            <div className="text-[10px] uppercase tracking-[0.18em] text-teal-300/70 mt-3">— Aignoz Founder Review AI</div>
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            <VerdictPill text={fr.overall_recommendation} />
            <span className="text-[10px] mono text-white/50">confidence {fr.confidence_score}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-3 flex-1 min-h-0">
        <div className="col-span-8 min-h-0">
          <RiskMatrix fr={fr} onSelect={setSelected} />
        </div>
        <GlassPanel className="col-span-4" title="Top 3 Improvements">
          <ol className="p-5 space-y-3 overflow-y-auto">
            {(fr.top_3_improvements || []).map((s, i) => (
              <li key={i} className="flex gap-3">
                <span className="shrink-0 w-7 h-7 rounded-full border border-teal-400/40 text-teal-200 text-sm flex items-center justify-center font-semibold">{i + 1}</span>
                <span className="text-sm text-white/85 leading-relaxed">{s}</span>
              </li>
            ))}
          </ol>
        </GlassPanel>
      </div>
    </div>
  );
}

function FounderInsights({ fr }) {
  if (!fr) return null;
  return (
    <div className="space-y-4">
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-2">Founder scores</div>
        <div className="space-y-2">
          {[["technical_feasibility_score", "Technical Feasibility"], ["clinical_adoption_score", "Clinical Adoption"], ["investor_attractiveness_score", "Investor Attractiveness"], ["competitive_advantage_score", "Competitive Advantage"]].map(([k, label]) => {
            const v = fr[k];
            return (
              <div key={k}>
                <div className="flex items-center justify-between text-xs mb-1"><span className="text-white/60">{label}</span><span className="mono text-teal-200">{v}/100</span></div>
                <div className="h-1 w-full bg-white/[0.06] rounded overflow-hidden"><div className="h-full bg-teal-400" style={{ width: `${v}%` }} /></div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="text-[10px] text-white/30">Click any risk dot for full mitigation.</div>
    </div>
  );
}
function RiskInsights({ risk }) {
  const c = severityClasses(risk.severity);
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/40">{risk.label} Risk</div>
        <SevPill severity={risk.severity} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.14em] text-white/40 mb-1">Explanation</div>
        <p className="text-sm text-white/85 leading-relaxed">{risk.explanation}</p>
      </div>
      <div className={`rounded-md border p-3 ${c.border} ${c.bg}`}>
        <div className="text-[10px] uppercase tracking-[0.14em] text-emerald-300 mb-1 inline-flex items-center gap-1"><Wrench className="w-3 h-3" />Mitigation</div>
        <p className="text-sm text-white/85 leading-relaxed">{risk.mitigation}</p>
      </div>
    </div>
  );
}

// ===========================================================================
// INVESTOR MODE — 3-column dashboard
// ===========================================================================
function InvestorMode({ doc, stream }) {
  const fr = stream.founderReview || doc.founder_review;
  const sc = stream.scores || doc.scores || {};
  const com = doc.outputs?.commercialization_agent;
  const fin = stream.finalReport || doc.final_report || {};
  if (!com || !fr) return <Empty label="Investor view unlocks once commercialization + founder review are ready." />;
  const topRisks = ["commercialization_risk", "regulatory_risk", "technical_risk"]
    .map((k) => ({ key: k, label: k.replace("_risk", "").replace(/^./, (c) => c.toUpperCase()), ...fr[k] }))
    .filter((r) => r.severity);
  return (
    <div className="h-full p-6 grid grid-cols-12 grid-rows-[1fr_auto] gap-3" data-testid="tab-investor">
      <GlassPanel className="col-span-3" title="Investor Attractiveness">
        <div className="p-5 flex flex-col items-center gap-3 h-full justify-center">
          <BigGauge value={sc.investor_attractiveness_score?.score} />
          <div className="text-xs text-white/70 text-center leading-relaxed">{sc.investor_attractiveness_score?.rationale}</div>
        </div>
      </GlassPanel>
      <div className="col-span-6 flex flex-col gap-3">
        <GlassPanel title="Market Stack">
          <div className="p-4 flex items-center justify-center">
            <MarketFunnel tam={com.tam_usd} sam={com.sam_usd} som={com.som_usd} large />
          </div>
        </GlassPanel>
        <GlassPanel title="Why Now">
          <div className="p-4">
            <div className="text-lg italic text-teal-100 leading-relaxed">{fin.pitch_deck_summary?.why_now || "—"}</div>
          </div>
        </GlassPanel>
      </div>
      <div className="col-span-3 flex flex-col gap-3">
        <GlassPanel title="Time to Market">
          <div className="p-4 flex items-center justify-center">
            <BigGauge value={sc.time_to_market_score?.score} small />
          </div>
        </GlassPanel>
        <GlassPanel title="Novelty">
          <div className="p-4 flex items-center justify-center">
            <BigGauge value={sc.scientific_novelty_score?.score} small />
          </div>
        </GlassPanel>
        <GlassPanel title="Top Risks">
          <div className="p-3 space-y-1.5">
            {topRisks.map((r) => (
              <div key={r.key} className="flex items-center gap-2 text-xs">
                <SevPill severity={r.severity} />
                <span className="text-white/70 truncate flex-1">{r.label}</span>
              </div>
            ))}
          </div>
        </GlassPanel>
      </div>
      <GlassPanel className="col-span-12" title="Pilot Partners · Commercial Roadmap">
        <div className="p-4 grid grid-cols-2 gap-4">
          <div className="flex flex-wrap gap-1.5">
            {(com.pilot_partners || []).map((p, i) => (
              <span key={i} className="text-[11px] px-2 py-1 rounded bg-white/[0.04] border border-white/[0.08]">{p}</span>
            ))}
          </div>
          <div className="text-sm text-white/70 leading-relaxed line-clamp-3">{com.commercialization_roadmap}</div>
        </div>
      </GlassPanel>
    </div>
  );
}
function BigGauge({ value, small = false }) {
  const v = value ?? 0;
  const size = small ? 90 : 150;
  const r = (size - 14) / 2;
  const C = 2 * Math.PI * r;
  const color = v >= 70 ? "#5EEAD4" : v >= 50 ? "#FBBF24" : "#F87171";
  return (
    <div className="relative">
      <svg width={size} height={size}>
        <circle cx={size/2} cy={size/2} r={r} stroke="rgba(255,255,255,0.06)" strokeWidth={6} fill="none" />
        <circle className="gauge-ring" cx={size/2} cy={size/2} r={r} stroke={color} strokeWidth={6} fill="none" strokeLinecap="round"
          strokeDasharray={C} strokeDashoffset={C * (1 - v/100)} transform={`rotate(-90 ${size/2} ${size/2})`} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="mono font-semibold" style={{ color, fontSize: small ? 22 : 36 }}><NumberCountUp value={value} /></span>
      </div>
    </div>
  );
}

// ===========================================================================
// SCORECARD — radar
// ===========================================================================
function Scorecard({ doc, stream }) {
  const sc = stream.scores || doc.scores;
  if (!sc) return <Empty label="Scorecard not available yet." />;

  // V3 5-Criteria Venture Filter axes (0-20 each).
  const V3 = [
    ["disease_burden_score", "Disease Burden"],
    ["market_size_score", "Market Size"],
    ["scientific_feasibility_score", "Scientific Feasibility"],
    ["patentability_score", "Patentability"],
    ["funding_availability_score", "Funding Availability"],
  ];
  const hasV3 = V3.some(([k]) => sc[k] && typeof sc[k].score === "number");
  const v3Total = hasV3 ? V3.reduce((s, [k]) => s + ((sc[k] && sc[k].score) || 0), 0) : 0;

  const sellableUSD = sc.sellable_idea_price_usd;
  const sellableINR = sc.sellable_idea_price_inr;
  const costSaved = sc.cost_saved_usd;
  const teamSize = sc.regulatory_team_size;

  // Legacy fallback for pre-V3 runs.
  const LEGACY = [
    ["innovation_score", "Innovation"],
    ["feasibility_score", "Feasibility"],
    ["regulatory_readiness_score", "Regulatory"],
    ["clinical_adoption_score", "Clinical"],
    ["investor_attractiveness_score", "Investor"],
    ["scientific_novelty_score", "Novelty"],
    ["time_to_market_score", "TTM"],
    ["technical_complexity_score", "Simplicity"],
  ];
  const hasLegacy = LEGACY.some(([k]) => sc[k] && typeof sc[k].score === "number");

  if (!hasV3 && !hasLegacy) {
    return (
      <div className="h-full p-6" data-testid="tab-scorecard">
        <div className="glass rounded-xl border-white/[0.06] p-8 text-center">
          <div className="text-base font-semibold mb-1">Pre-engine run</div>
          <div className="text-sm text-white/60">
            This venture was generated before the Aignoz 5-Criteria Venture Filter was introduced.
            Scorecard not available.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full p-6 overflow-y-auto" data-testid="tab-scorecard">
      <div className="grid grid-cols-12 gap-3">
        {/* V3 Filter — 8 columns */}
        <div className="col-span-12 lg:col-span-8 glass rounded-xl border-white/[0.06] p-5" data-testid="scorecard-v3">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-teal-300/80">Aignoz 5-Criteria Venture Filter</div>
              <div className="text-sm text-white/55 italic">5 × 20 = 100</div>
            </div>
            <div className="text-right">
              <div className="text-4xl mono font-semibold text-teal-100" data-testid="venture-filter-total">
                {hasV3 ? v3Total : "—"}
                <span className="text-base text-white/40">/100</span>
              </div>
              {!hasV3 && (
                <div className="text-[10px] text-white/45 mt-0.5">Legacy run — V3 unavailable</div>
              )}
            </div>
          </div>
          {hasV3 ? (
            <div className="space-y-3">
              {V3.map(([k, label]) => {
                const v = sc[k] || {};
                const score = v.score || 0;
                const pct = (score / 20) * 100;
                return (
                  <div key={k} data-testid={`v3-bar-${k}`}>
                    <div className="flex items-baseline justify-between mb-1">
                      <span className="text-sm font-medium text-white/85">{label}</span>
                      <span className="text-sm mono text-teal-200 font-semibold">{score}<span className="text-white/40">/20</span></span>
                    </div>
                    <div className="h-2 rounded-full bg-white/[0.04] overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-teal-400/70 to-cyan-300/70"
                        style={{ width: `${pct}%`, transition: "width 0.6s ease" }} />
                    </div>
                    {v.rationale && (
                      <div className="text-[11px] text-white/50 mt-1 leading-snug">{v.rationale}</div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="space-y-2">
              <div className="text-xs text-white/45 italic mb-2">Showing legacy V2 8-axis scorecard.</div>
              {LEGACY.map(([k, label]) => {
                const s = sc[k]; if (!s) return null;
                return (
                  <div key={k} className="flex items-center justify-between border-b border-white/[0.04] py-1.5">
                    <span className="text-sm text-white/80">{label}</span>
                    <span className="mono text-sm text-teal-200">{s.score}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Sellable Idea Price + Cost Saved — 4 columns */}
        <div className="col-span-12 lg:col-span-4 flex flex-col gap-3">
          {(sellableUSD || sellableINR) ? (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className="rounded-xl p-5 border cursor-help"
                    style={{
                      background: "linear-gradient(135deg, rgba(209,126,192,0.10), rgba(94,234,212,0.04))",
                      borderColor: "rgba(209,126,192,0.35)",
                    }}
                    data-testid="sellable-price-card"
                  >
                    <div className="text-[10px] uppercase tracking-[0.18em] mb-1" style={{ color: "#F0B6DD" }}>Sellable Idea Price</div>
                    <div className="text-3xl mono font-semibold text-white mt-1" data-testid="sellable-price-usd">{formatUSD(sellableUSD)}</div>
                    <div className="text-base mono mt-0.5" style={{ color: "#F0B6DD" }} data-testid="sellable-price-inr">
                      {sellableINR != null ? `₹${Math.round(sellableINR).toLocaleString("en-IN")}` : formatINR(sellableUSD)}
                    </div>
                    <div className="text-[10px] text-white/45 mt-2 italic">Cost saved ÷ regulatory team size</div>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="left" className="max-w-xs text-xs">
                  Cost saved by Aignoz OS ({formatUSD(costSaved)}) ÷ Regulatory team size ({teamSize}) = sellable price per idea.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <div className="glass rounded-xl border-white/[0.06] p-5">
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-1">Sellable Idea Price</div>
              <div className="text-sm text-white/55 italic mt-2">Not available for this run.</div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div className="glass rounded-xl border-white/[0.06] p-3" data-testid="cost-saved-tile">
              <div className="text-[9px] uppercase tracking-[0.14em] text-white/40">Cost saved</div>
              <div className="text-lg mono font-semibold mt-0.5 text-teal-200">{formatUSD(costSaved)}</div>
              <div className="text-[10px] mono text-white/45">{formatINR(costSaved)}</div>
            </div>
            <div className="glass rounded-xl border-white/[0.06] p-3" data-testid="reg-team-tile">
              <div className="text-[9px] uppercase tracking-[0.14em] text-white/40">Reg. team</div>
              <div className="text-lg mono font-semibold mt-0.5 text-teal-200">{teamSize ?? "—"}</div>
              <div className="text-[10px] text-white/45">headcount</div>
            </div>
          </div>

          <div className="rounded-md p-3 border border-white/[0.06] bg-white/[0.012]">
            <div className="text-[10px] uppercase tracking-[0.14em] text-white/45 mb-1">Aignoz Sellable Price Formula</div>
            <div className="text-[11px] mono text-white/70 leading-snug">
              cost_saved_usd ÷ max(regulatory_team_size, 1)
              <br />
              <span className="text-white/45">→ price per idea</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// WORKFLOW — xyflow graph with LIVE run experience
// ===========================================================================
function WorkflowTab({ doc, stream, id, refetch }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const status = stream.runStatus || doc.status;
  const isLive = status === "running";
  const isComplete = status === "complete";
  const isFailed = status === "failed";

  // Completed agent count for progress bar
  const completedCount = useMemo(() => {
    return AGENTS.filter((a) => stream.agents?.[a.event]?.completed || doc.outputs?.[a.event]).length;
  }, [stream.agents, doc.outputs]);
  const progressPct = Math.round((completedCount / AGENTS.length) * 100);

  // Find currently-active agent
  const activeAgent = useMemo(() => {
    const entries = Object.entries(stream.agents || {});
    const running = entries.find(([, v]) => v?.started && !v?.completed);
    return running ? running[0] : null;
  }, [stream.agents]);

  // Celebrate + auto-route on completion
  useEffect(() => {
    if (!isComplete) return;
    setShowCelebration(true);
    const t = setTimeout(() => navigate(`/projects/${id}/overview`), 1800);
    return () => clearTimeout(t);
  }, [isComplete, id, navigate]);

  // Retry on failure — re-run with same inputs
  const retry = async () => {
    try {
      const { run_id } = await apiPost("/innovate", doc.inputs);
      toast.success("Retrying with same inputs");
      navigate(`/projects/${run_id}/workflow`);
    } catch (e) { toast.error(e.message); }
  };

  const cancel = async () => {
    try { await apiPost(`/runs/${id}/cancel`, {}); toast.success("Cancelled"); refetch?.(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="h-full relative" data-testid="tab-workflow">
      {/* Reconnection banner */}
      {isLive && (stream.connectionState === "reconnecting" || stream.connectionState === "lost") && (
        <motion.div
          initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          data-testid="reconnect-banner"
          className={`absolute top-3 left-1/2 -translate-x-1/2 z-30 inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-[11px] backdrop-blur-xl border ${
            stream.connectionState === "lost"
              ? "bg-rose-400/10 border-rose-400/40 text-rose-100"
              : "bg-amber-400/10 border-amber-400/40 text-amber-100"
          }`}>
          {stream.connectionState === "lost" ? (
            <>
              <AlertOctagon className="w-3.5 h-3.5" />
              <span>Connection lost.</span>
              <button onClick={() => window.location.reload()} className="underline underline-offset-2 hover:text-white" data-testid="reload-page">Reload page</button>
            </>
          ) : (
            <>
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              <span>Connection interrupted — reconnecting…</span>
            </>
          )}
        </motion.div>
      )}

      {/* Top-right toolbar: live indicator / cancel / status */}
      <div className="absolute top-3 right-3 z-20 flex items-center gap-2">
        {isLive && (
          <>
            <span data-testid="live-indicator" className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-rose-400/10 border border-rose-400/30 text-rose-200 text-[11px] uppercase tracking-wider">
              <span className="w-1.5 h-1.5 rounded-full bg-rose-400 pulse-dot" />
              Streaming
            </span>
            <Button size="sm" variant="outline" onClick={cancel} data-testid="cancel-workflow"><X className="w-3 h-3 mr-1" />Cancel Run</Button>
          </>
        )}
        {isComplete && !showCelebration && (
          <Button size="sm" onClick={() => navigate(`/projects/${id}/overview`)} className="bg-teal-400 hover:bg-teal-300 text-slate-950 font-semibold" data-testid="view-venture-btn">
            View Venture <ArrowRight className="w-3 h-3 ml-1.5" />
          </Button>
        )}
      </div>

      {/* Failure card */}
      {isFailed && (
        <div className="absolute top-3 left-3 right-3 z-20 mx-auto max-w-2xl rounded-xl border border-rose-400/40 bg-rose-400/[0.06] backdrop-blur p-4" data-testid="failure-card">
          <div className="flex items-start gap-3">
            <AlertOctagon className="w-5 h-5 text-rose-300 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-rose-100">Run failed{activeAgent ? ` at ${activeAgent}` : ""}.</div>
              <div className="text-xs text-rose-200/70 mt-1 leading-relaxed">The pipeline stopped mid-flight. This is often an upstream LLM budget or rate-limit issue — retry once usage resets, or top up the Emergent LLM key.</div>
              <div className="mt-3 flex gap-2">
                <Button size="sm" onClick={retry} className="bg-rose-400 hover:bg-rose-300 text-slate-950 font-semibold" data-testid="retry-run">
                  <Rocket className="w-3 h-3 mr-1.5" />Retry
                </Button>
                <Button size="sm" variant="outline" onClick={() => navigate("/projects/new")} data-testid="new-after-fail">New venture</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Celebration on completion */}
      <AnimatePresence>
        {showCelebration && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="celebration"
            className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none">
            <motion.div
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: [0.6, 1.15, 1], opacity: [0, 1, 1] }}
              transition={{ duration: 0.9, ease: [0.2, 0.7, 0.25, 1] }}
              className="relative">
              <motion.div
                animate={{ scale: [1, 2.4], opacity: [0.4, 0] }}
                transition={{ duration: 1.4, repeat: 1, ease: "easeOut" }}
                className="absolute inset-0 rounded-full bg-teal-400/40 blur-2xl" />
              <div className="relative px-8 py-5 rounded-2xl border border-teal-400/40 bg-teal-400/[0.08] backdrop-blur-xl">
                <div className="text-[10px] uppercase tracking-[0.22em] text-teal-300 mb-1">Pipeline complete</div>
                <div className="text-2xl font-semibold text-teal-100">Venture Generated</div>
                <div className="text-xs text-white/60 mt-1">Routing to your Overview…</div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <WorkflowGraph doc={doc} stream={stream} onNodeClick={setOpen} />
      <Sheet open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <SheetContent data-testid="node-detail" className="bg-[#0F1116] border-white/[0.08] w-full sm:max-w-xl overflow-y-auto">
          {open && <NodeDetail node={open} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function LiveRunInsights({ stream, activeAgent, progressPct, completedCount }) {
  const cls = activeAgent ? (AGENT_BY_EVENT[activeAgent]?.cls || activeAgent) : "Initializing";
  const tail = (stream.tokenTail?.text || "").slice(-260);
  return (
    <div className="space-y-4">
      <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-rose-400/10 border border-rose-400/30 text-rose-200 text-[10px] uppercase tracking-wider">
        <span className="w-1.5 h-1.5 rounded-full bg-rose-400 pulse-dot" />Run in progress
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-1">Active agent</div>
        <div data-testid="active-agent" className="text-xl font-semibold tracking-tight text-teal-100">{cls}</div>
        {activeAgent && <div className="text-[10px] mono text-white/40 mt-0.5">{activeAgent}</div>}
      </div>

      <div>
        <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.14em] text-white/40 mb-1.5">
          <span>Progress</span><span className="mono text-teal-200">{completedCount}/{AGENTS.length}</span>
        </div>
        <div className="h-1.5 w-full bg-white/[0.06] rounded overflow-hidden">
          <motion.div data-testid="progress-bar"
            className="h-full bg-teal-400"
            initial={{ width: 0 }} animate={{ width: `${progressPct}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }} />
        </div>
      </div>

      {tail && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-1.5">Live tokens</div>
          <div data-testid="token-tail" className="rounded-md border border-white/[0.06] bg-black/30 p-2.5 mono text-[10.5px] text-teal-100/80 leading-relaxed max-h-44 overflow-hidden break-words">
            {tail}<span className="inline-block w-1.5 h-3 bg-teal-300 ml-0.5 align-middle pulse-dot" />
          </div>
        </div>
      )}
    </div>
  );
}
function NodeInsights({ node }) {
  return (
    <div className="space-y-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/40">Selected node</div>
      <div className="text-base font-semibold">{node.cls}</div>
      <div className="text-xs mono text-teal-300/80">{node.event}</div>
      <div className="text-xs text-white/60 leading-relaxed">{node.desc}</div>
      <div className="grid grid-cols-2 gap-2">
        <Stat label="Status" value={node.status} />
        <Stat label="Confidence" value={node.confidence ?? "—"} />
        <Stat label="Citations" value={node.citations || 0} />
        <Stat label="Duration" value={node.duration ? `${(node.duration/1000).toFixed(1)}s` : "—"} />
      </div>
    </div>
  );
}
function Stat({ label, value }) {
  return (<div className="rounded-md border border-white/[0.06] bg-white/[0.015] p-2"><div className="text-[9px] uppercase tracking-[0.14em] text-white/40">{label}</div><div className="text-sm mono text-white/85 mt-0.5">{value}</div></div>);
}
function WorkflowLegend({ stream, doc }) {
  const counts = AGENTS.reduce((acc, a) => {
    const s = stream.agents?.[a.event];
    const o = doc.outputs?.[a.event];
    if (s?.completed || o) acc.complete++;
    else if (s?.started) acc.running++;
    else acc.idle++;
    return acc;
  }, { complete: 0, running: 0, idle: 0 });
  return (
    <div className="space-y-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/40">Pipeline status</div>
      <div className="space-y-2">
        <LegendRow color="bg-emerald-400" label="Complete" value={counts.complete} />
        <LegendRow color="bg-teal-300" label="Running" value={counts.running} />
        <LegendRow color="bg-white/30" label="Pending" value={counts.idle} />
      </div>
      <div className="text-[11px] text-white/40">Click any node to inspect its parsed output, citations and confidence.</div>
    </div>
  );
}
function LegendRow({ color, label, value }) {
  return (<div className="flex items-center gap-2"><span className={`w-1.5 h-1.5 rounded-full ${color}`} /><span className="text-xs text-white/70 flex-1">{label}</span><span className="text-xs mono text-white/85">{value}</span></div>);
}
function NodeDetail({ node }) {
  const [showRaw, setShowRaw] = useState(false);
  return (
    <div className="space-y-4">
      <SheetHeader>
        <SheetTitle className="flex items-center gap-3"><span>{node.cls}</span><span className="mono text-xs text-white/40">{node.event}</span></SheetTitle>
      </SheetHeader>
      <p className="text-sm text-white/70">{node.desc}</p>
      <div className="flex gap-2"><SevTag label={`status: ${node.status}`} /><SevTag label={`conf: ${node.confidence ?? "—"}`} /></div>
      {!showRaw && node.output && (
        <div className="space-y-2 text-sm">
          {Object.entries(node.output).slice(0, 8).map(([k, v]) => (
            <div key={k} className="rounded-md border border-white/[0.06] bg-white/[0.015] p-3">
              <div className="text-[10px] uppercase tracking-[0.14em] text-white/40 mb-1">{k.replace(/_/g, " ")}</div>
              <div className="text-sm text-white/85 leading-relaxed">{typeof v === "string" ? v : Array.isArray(v) ? v.slice(0, 6).map((x) => typeof x === "string" ? x : JSON.stringify(x)).join(" · ") : JSON.stringify(v).slice(0, 200)}</div>
            </div>
          ))}
        </div>
      )}
      <button onClick={() => setShowRaw((v) => !v)} className="text-[10px] uppercase tracking-wider text-teal-300/80 hover:text-teal-200" data-testid="toggle-raw">
        {showRaw ? "Hide raw JSON" : "Show raw JSON"}
      </button>
      {showRaw && (
        <pre className="mono text-[11px] bg-white/[0.02] border border-white/[0.06] rounded-md p-3 max-h-[60vh] overflow-y-auto whitespace-pre-wrap">{JSON.stringify(node.output, null, 2)}</pre>
      )}
    </div>
  );
}
function SevTag({ label }) { return <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-white/[0.04] border border-white/[0.08] mono">{label}</span>; }

// ===========================================================================
// TRACE
// ===========================================================================
function TraceView({ id }) {
  const [data, setData] = useState(null);
  useEffect(() => { apiGet(`/project/${id}/trace`, { useCache: false }).then(setData).catch(() => setData({ trace: [] })); }, [id]);
  if (!data) return <Skeleton className="h-64 m-6" />;
  return (
    <div className="h-full p-6 overflow-y-auto" data-testid="tab-trace">
      <div className="glass rounded-xl border-white/[0.06] overflow-hidden">
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-wider text-white/40">
            <tr><th className="text-left p-2">Time</th><th className="text-left p-2">Agent</th><th className="text-left p-2">Event</th><th className="text-right p-2">Dur</th><th className="text-right p-2">Conf</th><th className="text-left p-2">Note</th></tr>
          </thead>
          <tbody>
            {data.trace.map((e, i) => (
              <tr key={i} className="border-t border-white/[0.04]">
                <td className="p-2 mono text-white/40">{(e.timestamp || "").slice(11, 19)}</td>
                <td className="p-2">{e.agent || "—"}</td>
                <td className="p-2"><span className="text-[10px] uppercase tracking-wider mono text-teal-200">{e.event}</span></td>
                <td className="p-2 text-right mono">{e.duration_ms != null ? `${(e.duration_ms/1000).toFixed(1)}s` : "—"}</td>
                <td className="p-2 text-right mono">{e.confidence ?? "—"}</td>
                <td className="p-2 text-white/60 max-w-md truncate">{e.note || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-white/30 mt-3">{data.trace?.length || 0} entries · {data.citation_disclaimer}</div>
    </div>
  );
}
